// src/components/ThemeBootstrapper.jsx
// Fires once per login session: fetches the user's saved THEMESET and applies it globally.
// If STARTUP/TENANT_ID aren't in localStorage yet (startup modal pending for new users),
// polls every 500ms until the tenantId is available, then fetches.
import { useEffect, useRef } from "react";
import { connect } from "react-redux";
import { getLibraryCardRequest } from "../actions/libraryCardActions";
import { getLibraryCards } from "../getters/libraryCardGetters";
import { getTenantId } from "../utils/authUtils";

const POLL_INTERVAL_MS = 500;
const POLL_MAX_ATTEMPTS = 20; // 10 seconds max

const ThemeBootstrapper = ({
  isAuthenticated,
  onThemeLoaded,
  getLibraryCardRequest,
  cardDetails,
}) => {
  const hasFetchedRef = useRef(false);
  const hasAppliedRef = useRef(false);
  const pollTimerRef = useRef(null);

  // Reset per-session refs on logout
  useEffect(() => {
    if (!isAuthenticated) {
      hasFetchedRef.current = false;
      hasAppliedRef.current = false;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }
  }, [isAuthenticated]);

  // On login: fetch THEMESET, polling until tenantId is available
  useEffect(() => {
    if (!isAuthenticated || hasFetchedRef.current) return;

    const doFetch = (tenantId) => {
      hasFetchedRef.current = true;
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
      getLibraryCardRequest({
        operationType: "GET_LIBRARY_CARD",
        tenantId,
        entityType: "THEMESET",
        entityId: tenantId,
      });
    };

    // Try immediately first
    const tenantId = getTenantId();
    if (tenantId) {
      doFetch(tenantId);
      return;
    }

    // TenantId not yet available — startup modal is still showing for this user.
    // Poll until it's set (user submits startup name) or we time out.
    let attempts = 0;
    pollTimerRef.current = setInterval(() => {
      attempts += 1;
      const id = getTenantId();
      if (id) {
        doFetch(id);
      } else if (attempts >= POLL_MAX_ATTEMPTS) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    }, POLL_INTERVAL_MS);

    return () => {
      if (pollTimerRef.current) {
        clearInterval(pollTimerRef.current);
        pollTimerRef.current = null;
      }
    };
  }, [isAuthenticated, getLibraryCardRequest]);

  // When DB result arrives: find selected theme (or first) and apply
  useEffect(() => {
    if (!cardDetails || !hasFetchedRef.current || hasAppliedRef.current) return;
    try {
      const parsed = JSON.parse(cardDetails);
      if (parsed?.themeSet?.length > 0) {
        hasAppliedRef.current = true;
        const selectedIdx = parsed.themeSet.findIndex((t) => t.isSelected);
        const theme = parsed.themeSet[selectedIdx >= 0 ? selectedIdx : 0];
        const themeWithTypography = parsed.typography
          ? { ...theme, typography: parsed.typography }
          : theme;
        onThemeLoaded(themeWithTypography);
        localStorage.setItem("THEME", JSON.stringify(themeWithTypography));
      }
      // If cardDetails isn't a themeSet (from another page's fetch), ignore it
    } catch (e) {
      // ignore parse errors
    }
  }, [cardDetails, onThemeLoaded]);

  return null;
};

const mapStateToProps = (state) => ({
  cardDetails: getLibraryCards(state),
});

const mapDispatchToProps = (dispatch) => ({
  getLibraryCardRequest: (payload) => dispatch(getLibraryCardRequest(payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ThemeBootstrapper);
